<template>
  <Navigation :title="$t('changePassword.title')" />

  <div class="pw-hero bg-finger">
    <div class="hero-spacer"></div>
  </div>

  <div class="pw-body">
    <div class="pw-form-card card-priadi">
      <p class="pw-form-subtitle">{{ $t('changePassword.subtitle') }}</p>

      <!-- Old password -->
      <div class="form-group">
        <label class="form-label-priadi" for="inputPasswordOld">{{ $t('changePassword.oldPassword') }}</label>
        <div class="pw-input-wrap">
          <input
            :type="showPasswordOld ? 'text' : 'password'"
            class="form-control-priadi"
            :class="{ 'is-invalid': errors.old_password }"
            id="inputPasswordOld"
            :placeholder="$t('passwordHint')"
            v-model="oldPassword" />
          <button
            type="button"
            class="pw-eye-btn"
            @click="togglePassword('old')"
            :aria-label="$t('changePassword.oldPassword')">
            <i class="bi" :class="showPasswordOld ? 'bi-eye' : 'bi-eye-slash'"></i>
          </button>
        </div>
        <p class="form-error-priadi" v-if="errors.old_password">{{ errors.old_password }}</p>
      </div>

      <!-- New password -->
      <div class="form-group">
        <label class="form-label-priadi" for="inputPasswordNew">{{ $t('changePassword.newPassword') }}</label>
        <div class="pw-input-wrap">
          <input
            :type="showPasswordNew ? 'text' : 'password'"
            class="form-control-priadi"
            :class="{ 'is-invalid': errors.new_password }"
            id="inputPasswordNew"
            :placeholder="$t('passwordHint')"
            v-model="newPassword" />
          <button
            type="button"
            class="pw-eye-btn"
            @click="togglePassword('new')"
            :aria-label="$t('changePassword.newPassword')">
            <i class="bi" :class="showPasswordNew ? 'bi-eye' : 'bi-eye-slash'"></i>
          </button>
        </div>
        <p class="form-error-priadi" v-if="errors.new_password">{{ errors.new_password }}</p>
      </div>

      <!-- Password hint -->
      <p class="pw-hint" :class="{ 'pw-hint-met': newPassword.length >= 8 }" v-if="newPassword.length > 0">
        <i class="bi" :class="newPassword.length >= 8 ? 'bi-check-circle-fill' : 'bi-circle'"></i>
        {{ $t('validation.minLength', { field: $t('password'), min: '8' }) }}
      </p>

      <!-- Submit -->
      <button
        class="btn-priadi btn-priadi-block pw-submit"
        :class="{ disabled: !allValid || isLoading }"
        :disabled="isLoading"
        @click="submitPassword">
        <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
        {{ $t('changePassword.title') }}
      </button>
    </div>
  </div>
</template>

<script>
import Navigation from '../components/Navigation'
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'ChangePassword',
  components: { Navigation },
  data() {
    return {
      showPasswordOld: false,
      showPasswordNew: false,
      errors: {},
      oldPassword: '',
      newPassword: '',
      isLoading: false,
    }
  },
  computed: {
    allValid() {
      return this.newPassword.length >= 8
    },
  },
  mounted() {
    document.body.classList.add('body-tinted')
  },
  unmounted() {
    document.body.classList.remove('body-tinted')
  },
  methods: {
    togglePassword(name) {
      if (name === 'new') {
        this.showPasswordNew = !this.showPasswordNew
      } else {
        this.showPasswordOld = !this.showPasswordOld
      }
    },
    async submitPassword() {
      this.errors = {}

      if (!this.oldPassword) {
        this.errors.old_password = this.$t('validation.required', { field: this.$t('changePassword.oldPassword') })
      }
      if (!this.newPassword) {
        this.errors.new_password = this.$t('validation.required', { field: this.$t('changePassword.newPassword') })
      }

      if (this.oldPassword && this.newPassword) {
        this.isLoading = true
        try {
          const response = await axios.post(
            '/api/change_password',
            {
              user_id: this.$store.getters.getUser.id,
              old_password: this.oldPassword,
              new_password: this.newPassword,
            },
            { withCredentials: true }
          )
          Swal.fire({
            icon: response.data.status ? 'success' : 'error',
            title: response.data.status ? 'Success' : 'Error',
            text: response.data.message,
            showConfirmButton: false,
            timer: 1500,
          })
          if (response.data.status) this.$router.push('/profile')
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to change password',
          })
        } finally {
          this.isLoading = false
        }
      }
    },
  },
}
</script>

<style scoped>
/* Hero */
.pw-hero {
  position: relative;
}

/* Body content */
.pw-body {
  padding: 0 var(--space-4) calc(var(--space-16) + var(--space-10));
}

/* Form card — overlaps hero */
.pw-form-card {
  padding: var(--space-6);
  margin-top: -4em;
  position: relative;
  z-index: 1;
}

.pw-form-subtitle {
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--space-6);
}

/* Form groups */
.form-group {
  margin-bottom: var(--space-4);
}

/* Password input with eye button */
.pw-input-wrap {
  position: relative;
}

.pw-input-wrap .form-control-priadi {
  padding-right: var(--space-10);
}

.pw-eye-btn {
  position: absolute;
  right: var(--space-1);
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: var(--text-md);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--transition-base);
}

.pw-eye-btn:hover {
  color: var(--color-text-primary);
}

/* Validation hint */

.pw-hint {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-bottom: var(--space-2);
}

.pw-hint:last-child {
  margin-bottom: 0;
}

.pw-hint i {
  font-size: var(--text-sm);
  flex-shrink: 0;
}

.pw-hint-met {
  color: var(--color-success);
}

.pw-hint-met i {
  color: var(--color-success);
}

/* Submit button */
.pw-submit {
  margin-top: var(--space-6);
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
}
</style>
